import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { landingSectionOneApi, LandingSectionOne, productSectionApi, ProductSection, intelligentDesignSectionApi, IntelligentDesignSection, featuresSectionApi, FeaturesSection, testimonialsSectionApi, TestimonialsSection, whoSectionApi, WhoSection, faqSectionApi, FaqSection, pricingSectionApi, PricingSection, waitingSectionApi, WhatAreYouWaitingForSection, footerApi, Footer } from "@/services/api";
import heroImage from "@/assets/hero-nutrition.jpg";

const sectionIds = [
  'hero-section', 'bestsellers-section', 'intelligent-design-section', 'features-section', 'testimonials-section', 'who-section', 'pricing-section', 'faq-section', 'waiting-section'
];

const Landing = () => {
  const [current, setCurrent] = useState(0);
  const [heroContent, setHeroContent] = useState<LandingSectionOne>({
    title: "CLEANSE, DETOX AND CRUSH YOUR GOALS EVERY DAY. NATURALLY!",
    subtitle: "Take charge of your health and get the motivation you need to become the best version of yourself with nothing more than small, natural steps (and a few new ingredients).",
    mainImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
    description: "START YOUR JOURNEY TODAY",
  });
  const [productSection, setProductSection] = useState<ProductSection>({
    sectionTitle: "DON'T MISS OUR BESTSELLING PRODUCTS",
    sectionSubtitle: "There's much more for you to discover!",
    products: []
  });
  const [intelligentDesignSection, setIntelligentDesignSection] = useState<IntelligentDesignSection>({
    heading: "INTELLIGENT BY DESIGN — BUILT FOR THE FUTURE OF HEALTHCARE",
    description: "We believe that modern healthcare deserves modern tools. Doctor System was built from the ground up with a focus on intuitive design, robust security, and seamless workflow integration. Our platform is crafted to empower healthcare professionals, automating the mundane so you can focus on what truly matters: your patients. Every feature is thoughtfully designed to be both powerful and easy to use, ensuring your practice runs more efficiently and effectively.",
    techLogos: [
      { text: "HIPAA COMPLIANT" },
      { text: "GDPR READY" },
      { text: "ISO 27001 CERTIFIED" }
    ],
    imageUrl: "https://nmc.ae/_next/image?url=https%3A%2F%2Fstatic-cdn.nmc.ae%2Fstrapi%2FDietetics_and_Nutrition_Getty_Images_690750224_706x500px_92aead0cbf.jpg&w=3840&q=75"
  });
  const [featuresSection, setFeaturesSection] = useState<FeaturesSection>({
    heading: "NOT A DIET, JUST SMART HEALTHCARE",
    subtitle: "Delivered in Features That Fit Perfectly Around Your Practice.",
    features: [
      {
        iconUrl: "https://img.icons8.com/fluency/96/doctor-male.png",
        title: "AI Appointment Scheduling",
        description: "Automate bookings, reminders, and reduce no-shows with our smart AI-powered scheduler."
      },
      {
        iconUrl: "https://img.icons8.com/fluency/96/medical-history.png",
        title: "Complete Patient Records",
        description: "Access and update patient history, notes, and files securely from anywhere, anytime."
      },
      {
        iconUrl: "https://img.icons8.com/fluency/96/online-support.png",
        title: "Telemedicine & Video Calls",
        description: "Offer virtual consultations and follow-ups with secure, integrated video calls."
      },
      {
        iconUrl: "https://img.icons8.com/fluency/96/financial-growth-analysis.png",
        title: "Practice Analytics",
        description: "Track revenue, patient flow, and key metrics to grow your practice efficiently."
      },
      {
        iconUrl: "https://img.icons8.com/fluency/96/lock-2.png",
        title: "HIPAA-Grade Security",
        description: "Your data is protected with industry-leading encryption and compliance standards."
      },
      {
        iconUrl: "https://img.icons8.com/fluency/96/online-payment-.png",
        title: "Automated Billing",
        description: "Streamline payments, insurance claims, and invoicing with ease."
      }
    ],
    ctaButton: "START YOUR JOURNEY TODAY 🍃",
    ratingLabel: "OVER 1 MILLION USERS WORLDWIDE"
  });

  const [testimonialsSection, setTestimonialsSection] = useState<TestimonialsSection>({
    heading: "IN THE EVER-GROWING COMMUNITY OF OVER 1 MILLION CLIENTS WHO LOVE THE BENEFITS OF DOCTOR SYSTEM!",
    ratingLabel: "",
    testimonials: [
      {
        photo: "https://randomuser.me/api/portraits/women/44.jpg",
        name: "Erin",
        role: "Clinic Owner",
        title: "WORKS AND TASTES GREAT!",
        quote: "If you love smart healthcare and want to save time, this is really effective. I tried Doctor System and I must say I'm lovin' how it streamlines my clinic! I use it every day and it's a game changer! Will definitely subscribe again :)"
      },
      {
        photo: "https://randomuser.me/api/portraits/men/32.jpg",
        name: "Dr. Alex",
        role: "Family Physician",
        title: "INCREDIBLE SUPPORT",
        quote: "The support team is amazing and the features are exactly what my practice needed. Highly recommended for any clinic!"
      },
      {
        photo: "https://randomuser.me/api/portraits/women/65.jpg",
        name: "Sarah",
        role: "Practice Manager",
        title: "SAVED US HOURS EVERY WEEK",
        quote: "Doctor System helped us automate so many tasks. Our staff and patients are happier, and we save hours every week!"
      }
    ]
  });

  const [whoSection, setWhoSection] = useState<WhoSection>({
    heading: "COMMITTED TO PROVIDING ONLY THE HIGHEST QUALITY",
    subtitle: "Our platform is developed by experts to satisfy your needs and help your clinic thrive, every time!",
    items: [
      {
        iconUrl: "https://img.icons8.com/color/96/guarantee.png",
        title: "30 DAYS MONEY BACK GUARANTEE",
        description: "We believe in our platform so much that we're willing to take all the risk for you! If for any reason at all, you're not satisfied, just email us and we'll refund you once we receive your request."
      },
      {
        iconUrl: "https://img.icons8.com/color/96/organic-food.png",
        title: "ALL-IN-ONE SOLUTION",
        description: "Every feature in Doctor System is designed to be intuitive, secure, and to help you run your practice more efficiently. You can rest assured your data is safe and your workflow is smooth!"
      },
      {
        iconUrl: "https://img.icons8.com/color/96/great-britain.png",
        title: "PROUDLY MADE FOR CLINICS",
        description: "Doctor System is built for clinics and healthcare professionals, with world-class quality, compliance, and support. Our team is always here to help you succeed!"
      }
    ],
    ctaButton: "START YOUR JOURNEY TODAY 🍃",
    ratingLabel: ""
  });

  const [faqSection, setFaqSection] = useState<FaqSection>({
    heading: "GOT ANY QUESTIONS? WE'VE GOT THE ANSWERS!",
    subtitle: "Find answers to the most common questions about Nuvamaj below.",
    faqs: [
      {
        question: "Will Nuvamaj help me grow my clinic?",
        answer: "Absolutely! Our platform is designed to streamline your workflow, improve patient satisfaction, and help you grow your business."
      },
      {
        question: "Is my data secure?",
        answer: "Yes, we use HIPAA-grade security and encryption to keep your data safe and private at all times."
      },
      {
        question: "Can I use Nuvamaj on mobile?",
        answer: "Of course! Our platform is fully responsive and works on any device, anywhere."
      },
      {
        question: "How do I get support?",
        answer: "You can reach our support team 24/7 via chat, email, or phone. We're always here to help!"
      },
      {
        question: "Is there a free trial?",
        answer: "Yes! You can try Nuvamaj free for 30 days, no credit card required."
      }
    ],
    ratingLabel: "Rated Excellent by 1,000+ Clinics"
  });

  const [pricingSection, setPricingSection] = useState<PricingSection>({
    heading: "Choose Your Plan",
    subtitle: "Pick the plan that's right for you. Get started for free, or unlock powerful pro features.",
    plans: [
      {
        name: "Starter",
        price: "TK0",
        description: "For individuals and small teams getting started with our system.",
        features: [
          "Core Features",
          "10 Client Profiles",
          "Basic Analytics",
          "24/7 Support"
        ],
        buttonText: "Get Started",
        isPopular: false,
        popularBadge: "MOST POPULAR"
      },
      {
        name: "Pro",
        price: "TK5,500",
        description: "For professionals who need advanced tools and full capabilities.",
        features: [
          "All Core Features",
          "Unlimited Clients",
          "Advanced Analytics & Reports",
          "24/7 Priority Support"
        ],
        buttonText: "Choose Pro",
        isPopular: true,
        popularBadge: "MOST POPULAR"
      },
      {
        name: "Enterprise",
        price: "TK20,000",
        description: "For large organizations with custom needs and dedicated support.",
        features: [
          "Everything in Pro",
          "Custom Integrations",
          "Dedicated Account Manager",
          "On-Premise Options"
        ],
        buttonText: "Contact Us",
        isPopular: false,
        popularBadge: "MOST POPULAR"
      }
    ]
  });

  const [waitingSection, setWaitingSection] = useState<WhatAreYouWaitingForSection | null>(null);

  const [footer, setFooter] = useState<Footer | null>(null);

  const [openFAQ, setOpenFAQ] = React.useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = React.useState(false);

  // Fetch hero content from database
  useEffect(() => {
    landingSectionOneApi.get()
      .then((data) => setHeroContent(data))
      .catch((err) => {
        console.error('Error fetching hero content:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch product section content from database
  useEffect(() => {
    productSectionApi.get()
      .then((data) => setProductSection(data))
      .catch((err) => {
        console.error('Error fetching product section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch intelligent design section content from database
  useEffect(() => {
    intelligentDesignSectionApi.get()
      .then((data) => setIntelligentDesignSection(data))
      .catch((err) => {
        console.error('Error fetching intelligent design section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch features section content from database
  useEffect(() => {
    featuresSectionApi.get()
      .then((data) => setFeaturesSection(data))
      .catch((err) => {
        console.error('Error fetching features section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch testimonials section content from database
  useEffect(() => {
    testimonialsSectionApi.get()
      .then((data) => setTestimonialsSection(data))
      .catch((err) => {
        console.error('Error fetching testimonials section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch who section content from database
  useEffect(() => {
    whoSectionApi.get()
      .then((data) => setWhoSection(data))
      .catch((err) => {
        console.error('Error fetching who section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch FAQ section content from database
  useEffect(() => {
    faqSectionApi.get()
      .then((data) => setFaqSection(data))
      .catch((err) => {
        console.error('Error fetching FAQ section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  // Fetch pricing section content from database
  useEffect(() => {
    pricingSectionApi.get()
      .then((data) => setPricingSection(data))
      .catch((err) => {
        console.error('Error fetching pricing section:', err);
        // Keep default content if fetch fails
      });
  }, []);

  useEffect(() => {
    waitingSectionApi.get().then(setWaitingSection).catch(() => {
      setWaitingSection({
        headline: 'WHAT ARE YOU WAITING FOR?',
        description: "It's time to experience the Nuvamaj difference for yourself. Get the energy, focus, and results you need to boost your clinic's performance and achieve the success you've always dreamed of!",
        ctaText: 'START YOUR JOURNEY TODAY',
        ratingLabel: '',
      });
    });
  }, []);

  useEffect(() => {
    footerApi.get().then(setFooter).catch(() => {
      setFooter({
        brand: {
          logoText: 'DOCTOR SYSTEM',
          disclaimer: 'Disclaimer: Doctor System is not a substitute for medical advice. Always consult a healthcare professional for medical concerns.',
          socialLinks: [
            { platform: 'Facebook', url: '#', icon: 'https://img.icons8.com/color/32/facebook-new.png' },
            { platform: 'Twitter', url: '#', icon: 'https://img.icons8.com/color/32/twitter--v1.png' },
            { platform: 'Instagram', url: '#', icon: 'https://img.icons8.com/color/32/instagram-new.png' }
          ]
        },
        quickLinks: [
          { text: 'Shop', url: '#' },
          { text: 'Cart', url: '#' },
          { text: 'Privacy Policy', url: '#' },
          { text: 'Terms & Conditions', url: '#' },
          { text: 'Blog', url: '#' },
          { text: 'Trade & Wholesale', url: '#' }
        ],
        customerService: [
          { text: 'Contact Us', url: '#' },
          { text: 'Delivery', url: '#' },
          { text: 'Refund Policy', url: '#' },
          { text: 'My Account', url: '#' }
        ],
        paymentMethods: [
          { name: 'PayPal', icon: 'https://img.icons8.com/color/32/paypal.png' },
          { name: 'Visa', icon: 'https://img.icons8.com/color/32/visa.png' },
          { name: 'Mastercard', icon: 'https://img.icons8.com/color/32/mastercard-logo.png' },
          { name: 'Discover', icon: 'https://img.icons8.com/color/32/discover.png' },
          { name: 'Amex', icon: 'https://img.icons8.com/color/32/amex.png' }
        ]
      });
    });
  }, []);

  const total = productSection.products.length;
  
  const getVisibleProducts = (current, perPage) => {
    if (productSection.products.length === 0) return [];
    let visible = [];
    for (let i = 0; i < perPage; i++) {
      visible.push(productSection.products[(current + i) % total]);
    }
    return visible;
  };

  // Responsive: 3 on desktop, 1 on mobile
  const [perPage, setPerPage] = useState(3);
  React.useEffect(() => {
    const handleResize = () => {
      setPerPage(window.innerWidth < 700 ? 1 : 3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const prevProduct = () => setCurrent((current - 1 + total) % total);
  const nextProduct = () => setCurrent((current + 1) % total);
  const visibleProducts = getVisibleProducts(current, perPage);

  const [testimonialIndex, setTestimonialIndex] = React.useState(0);
  const prevTestimonial = () => setTestimonialIndex((testimonialIndex - 1 + testimonialsSection.testimonials.length) % testimonialsSection.testimonials.length);
  const nextTestimonial = () => setTestimonialIndex((testimonialIndex + 1) % testimonialsSection.testimonials.length);

  // Section animation logic
  const sectionRefs = useRef([]);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.15 }
    );
    sectionRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setShowOptionsMenu(false);
    }
  };

  return (
    <div>
      {/* Announcement Bar */}
      <div id="announcement-bar">WELCOME TO NUVAMAJ</div>

      {/* Menu Bar */}
      <header id="menu-bar">
        <div id="menu-bar-inner">
          {/* Left: Logo */}
          <div id="logo">
            NUVAMAJ
          </div>
          
          {/* Center: Menu */}
          <nav id="menu-nav">
            <div className="options-dropdown">
              <button 
                className="options-button"
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                onBlur={() => setTimeout(() => setShowOptionsMenu(false), 200)}
              >
                OPTIONS
                <svg className="dropdown-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {showOptionsMenu && (
                <div className="options-menu">
                  <button onClick={() => scrollToSection('features-section')}>Features</button>
                  <button onClick={() => scrollToSection('testimonials-section')}>Testimonials</button>
                  <button onClick={() => scrollToSection('pricing-section')}>Pricing</button>
                  <button onClick={() => scrollToSection('faq-section')}>FAQ</button>
                  <button onClick={() => scrollToSection('waiting-section')}>Get Started</button>
                </div>
              )}
            </div>
            <a href="#" onClick={() => scrollToSection('intelligent-design-section')}>ABOUT</a>
            <a href="#" onClick={() => scrollToSection('faq-section')}>FAQ</a>
            <a href="#" onClick={() => scrollToSection('pricing-section')}>PRICING</a>
          </nav>

          {/* Right: Icons */}
          <div id="menu-icons">
            <span className="icon-circle" onClick={() => navigate('/auth')} style={{cursor:'pointer'}} title="Sign In / Sign Up">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8bc34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M21 20c0-2.8-4-5-9-5s-9 2.2-9 5"/></svg>
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero-section" ref={el => sectionRefs.current[0] = el} className="fade-section">
        <div 
          id="hero-bg" 
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.8)), url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        <div id="hero-content">
          <h1>{heroContent.title}</h1>
          <p id="hero-sub">{heroContent.subtitle}</p>
          <button id="hero-cta">{heroContent.description}</button>
        </div>
      </section>

      {/* Intelligent Design Section */}
      <section id="intelligent-design-section" ref={el => sectionRefs.current[2] = el} className="fade-section">
        <div id="intelligent-design-content">
          <div className="intelligent-design-text">
            <h2>{intelligentDesignSection.heading}</h2>
            <p>{intelligentDesignSection.description}</p>
          </div>
          <div className="intelligent-design-image">
            <img 
              src={intelligentDesignSection.imageUrl} 
              alt="Intelligent Design"
              className="design-image"
            />
          </div>
          <div className="tech-logos">
            {intelligentDesignSection.techLogos.map((logo, idx) => (
              <div key={idx} className="tech-logo-item">{logo.text}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" ref={el => sectionRefs.current[3] = el} className="fade-section">
        <h2>{featuresSection.heading}</h2>
        <p className="features-sub">{featuresSection.subtitle}</p>
        <div id="features-grid">
          {featuresSection.features.map((feature, idx) => (
            <div key={idx} className="feature-item">
              <div className="feature-img"><img src={feature.iconUrl} alt={feature.title} /></div>
              <div className="feature-title">{feature.title}</div>
              <div className="feature-desc">{feature.description}</div>
            </div>
          ))}
        </div>
        <button id="features-cta">{featuresSection.ctaButton}</button>

      </section>

      {/* Testimonials Section */}
      <section id="testimonials-section" ref={el => sectionRefs.current[4] = el} className="fade-section">
        <div id="testimonials-bg"></div>
        <div id="testimonials-content">
          <h2>{testimonialsSection.heading}</h2>
         
          <div id="testimonial-carousel">
            <button className="carousel-arrow" onClick={prevTestimonial}>&#60;</button>
            <div className="testimonial-main">
              <img className="testimonial-photo" src={testimonialsSection.testimonials[testimonialIndex]?.photo} alt={testimonialsSection.testimonials[testimonialIndex]?.name} />
              <div className="testimonial-text">
                <div className="testimonial-title">{testimonialsSection.testimonials[testimonialIndex]?.title}</div>
                <div className="testimonial-quote">"{testimonialsSection.testimonials[testimonialIndex]?.quote}"</div>
                <div className="testimonial-author">{testimonialsSection.testimonials[testimonialIndex]?.name} <span className="testimonial-role">{testimonialsSection.testimonials[testimonialIndex]?.role}</span></div>
              </div>
            </div>
            <button className="carousel-arrow" onClick={nextTestimonial}>&#62;</button>
          </div>
          <div className="testimonial-dots">
            {testimonialsSection.testimonials.map((_, idx) => (
              <span key={idx} className={idx === testimonialIndex ? 'dot active' : 'dot'}></span>
            ))}
          </div>
        </div>
      </section>

      {/* Who Are We Section */}
      <section id="who-section" ref={el => sectionRefs.current[5] = el} className="fade-section">
        <h2>{whoSection.heading}</h2>
        <p className="who-sub">{whoSection.subtitle}</p>
        <div id="who-grid">
          {whoSection.items.map((item, index) => (
            <div key={index} className="who-item">
              <img src={item.iconUrl} alt={item.title} />
              <div className="who-title">{item.title}</div>
              <div className="who-desc">{item.description}</div>
            </div>
          ))}
        </div>
        <button id="who-cta">{whoSection.ctaButton}</button>

      </section>

      {/* FAQ Section */}
      <section id="faq-section" ref={el => sectionRefs.current[6] = el} className="fade-section">
        <h2>{faqSection.heading}</h2>
        <p className="faq-sub">{faqSection.subtitle}</p>
        <div id="faq-accordion">
          {faqSection.faqs.map((item, idx) => (
            <div className="faq-item" key={idx}>
              <button className="faq-question" onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}>
                <span>{item.question}</span>
                <div className={openFAQ === idx ? 'faq-icon open' : 'faq-icon'}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </div>
              </button>
              <div className={openFAQ === idx ? 'faq-answer-wrapper open' : 'faq-answer-wrapper'}>
                <div className="faq-answer">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

      </section>

      {/* Pricing Section */}
      <section id="pricing-section" ref={el => sectionRefs.current[7] = el} className="fade-section">
        <h2>{pricingSection.heading}</h2>
        <p className="subtitle">{pricingSection.subtitle}</p>
        <div id="pricing-cards">
          {pricingSection.plans.map((plan, index) => (
            <div key={index} className={`pricing-card ${plan.isPopular ? 'pro' : ''}`}>
              {plan.isPopular && (
                <div className="popular-badge">{plan.popularBadge}</div>
              )}
              <h3>{plan.name}</h3>
              <div className="price">{plan.price}<span>/month</span></div>
              <p className="description">{plan.description}</p>
              <ul>
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>&#10003; {feature}</li>
                ))}
              </ul>
              <button className="cta-btn">{plan.buttonText}</button>
            </div>
          ))}
        </div>
      </section>

      {/* What Are You Waiting For Section */}
      <section id="waiting-section" ref={el => sectionRefs.current[8] = el} className="fade-section">
        <div id="waiting-content">
          <h2>{waitingSection?.headline || 'WHAT ARE YOU WAITING FOR?'}</h2>
          <p>{waitingSection?.description || ''}</p>
          <button id="waiting-cta">{waitingSection?.ctaText || 'START YOUR JOURNEY TODAY'}</button>
          
        </div>
      </section>

      {/* Footer */}
      <footer id="footer">
        <div id="footer-main">
          <div className="footer-col footer-brand">
            <div className="footer-logo">{footer?.brand.logoText || 'DOCTOR'}</div>
            <div className="footer-social">
              {footer?.brand.socialLinks.map((link, index) => (
                <a key={index} href={link.url}>
                  <img src={link.icon} alt={link.platform} />
                </a>
              ))}
            </div>
            <div className="footer-disclaimer">{footer?.brand.disclaimer || 'Disclaimer: Doctor System is not a substitute for medical advice. Always consult a healthcare professional for medical concerns.'}</div>
          </div>
          <div className="footer-col footer-links">
            <div className="footer-title">QUICK LINKS</div>
            {footer?.quickLinks.map((link, index) => (
              <a key={index} href={link.url}>{link.text}</a>
            ))}
          </div>
          <div className="footer-col footer-service">
            <div className="footer-title">CUSTOMER SERVICE</div>
            {footer?.customerService.map((link, index) => (
              <a key={index} href={link.url}>{link.text}</a>
            ))}
            <div className="footer-payments">
              {footer?.paymentMethods.map((payment, index) => (
                <img key={index} src={payment.icon} alt={payment.name} />
              ))}
            </div>
          </div>
        </div>
        <div className="footer-copyright">© {new Date().getFullYear()} Nuvamaj. All rights reserved.</div>
      </footer>

      {/* CSS Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

        :root {
          --primary: #10b981;
          --primary-foreground: #ffffff;
          --background: #ffffff;
          --foreground: #0f172a;
          --muted: #64748b;
          --muted-foreground: #64748b;
          --border: #e2e8f0;
          --input: #e2e8f0;
          --ring: #10b981;
          --radius: 0.5rem;
          --shadow-card: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
          --shadow-glow: 0 0 20px rgb(16 185 129 / 0.3);
        }

        body {
          margin: 0;
          padding: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, 'Noto Sans', 'Helvetica Neue', sans-serif;
          background-color: var(--background);
          color: var(--foreground);
        }

        .section-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }

        .fade-in {
          animation: fadeIn 0.8s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .btn-hero {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--shadow-glow);
        }

        .btn-hero:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgb(16 185 129 / 0.4);
        }

        .btn-secondary {
          background: transparent;
          color: var(--foreground);
          border: 2px solid var(--border);
          border-radius: 9999px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: var(--foreground);
          color: var(--background);
          border-color: var(--foreground);
        }

        .card-elevated {
          background: var(--background);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow-card);
          transition: all 0.2s;
        }

        .card-elevated:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgb(0 0 0 / 0.1);
        }

        .bg-soft {
          background-color: #f8fafc;
        }

        #announcement-bar {
          background: var(--primary);
          color: var(--primary-foreground);
          text-align: center;
          font-size: 0.875rem;
          padding: 0.5rem 0;
          font-weight: 600;
        }

        #menu-bar {
          background: var(--background);
          box-shadow: var(--shadow-card);
          height: 4rem;
          position: sticky;
          top: 0;
          z-index: 50;
        }

        #menu-bar-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1200px;
          margin: 0 auto;
          height: 100%;
          padding: 0 2rem;
        }

        #logo {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--primary);
          letter-spacing: 0.1em;
        }

        #menu-nav {
          display: flex;
          gap: 2rem;
          align-items: center;
        }

        #menu-nav a, .options-button {
          text-decoration: none;
          color: var(--muted-foreground);
          font-weight: 500;
          transition: color 0.2s;
          background: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }

        #menu-nav a:hover, .options-button:hover {
          color: var(--primary);
        }

        .options-dropdown {
          position: relative;
          display: inline-block;
        }

        .options-button {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0;
        }

        .dropdown-arrow {
          transition: transform 0.2s;
        }

        .options-button:hover .dropdown-arrow {
          transform: rotate(180deg);
        }

        .options-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--background);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: 0 4px 12px rgb(0 0 0 / 0.1);
          min-width: 160px;
          z-index: 1000;
          overflow: hidden;
        }

        .options-menu button {
          display: block;
          width: 100%;
          padding: 0.75rem 1rem;
          text-align: left;
          background: none;
          border: none;
          color: var(--muted-foreground);
          font-size: 0.875rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .options-menu button:hover {
          background: #f8fafc;
          color: var(--primary);
        }

        #menu-icons {
          display: flex;
          gap: 1rem;
        }

        .icon-circle {
          background: #f0fdf4;
          border-radius: 50%;
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          transition: all 0.2s;
        }

        .icon-circle:hover {
          background: var(--primary);
          color: var(--primary-foreground);
          transform: scale(1.1);
        }

        /* Hero Section */
        #hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding-top: 5rem;
        }

        #hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }

        #hero-content {
          position: relative;
          z-index: 10;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        #hero-content h1 {
          font-size: clamp(2.5rem, 5vw, 4.5rem);
          font-weight: 800;
          color: var(--foreground);
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        #hero-sub {
          font-size: clamp(1.125rem, 2vw, 1.5rem);
          color: var(--muted-foreground);
          margin-bottom: 2rem;
          line-height: 1.6;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        #hero-cta {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          padding: 1rem 2.5rem;
          font-size: 1.125rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--shadow-glow);
          margin-bottom: 2rem;
        }

        #hero-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgb(16 185 129 / 0.4);
        }

        /* Intelligent Design Section */
        #intelligent-design-section {
          padding: 5rem 0;
          background: var(--background);
        }

        #intelligent-design-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
        }

        .intelligent-design-text h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .intelligent-design-text p {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .intelligent-design-image {
          text-align: center;
        }

        .design-image {
          width: 100%;
          max-width: 500px;
          height: auto;
          border-radius: 1rem;
          box-shadow: var(--shadow-card);
        }

        .tech-logos {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          justify-content: center;
        }

        .tech-logo-item {
          background: var(--primary);
          color: var(--primary-foreground);
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
        }

        /* Features Section */
        #features-section {
          padding: 5rem 0;
          background: #f8fafc;
        }

        #features-section h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          text-align: center;
          margin-bottom: 1rem;
        }

        .features-sub {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          text-align: center;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        #features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto 3rem auto;
          padding: 0 1rem;
        }

        .feature-item {
          background: var(--background);
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: var(--shadow-card);
          border: 1px solid var(--border);
          transition: all 0.2s;
          text-align: center;
        }

        .feature-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgb(0 0 0 / 0.1);
        }

        .feature-img {
          width: 4rem;
          height: 4rem;
          background: #f0fdf4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem auto;
        }

        .feature-img img {
          width: 2.5rem;
          height: 2.5rem;
          object-fit: contain;
        }

        .feature-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--foreground);
          margin-bottom: 0.75rem;
        }

        .feature-desc {
          color: var(--muted-foreground);
          line-height: 1.6;
        }

        #features-cta {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          padding: 1rem 2.5rem;
          font-size: 1.125rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--shadow-glow);
          display: block;
          margin: 0 auto;
        }

        #features-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgb(16 185 129 / 0.4);
        }

        /* Testimonials Section */
        #testimonials-section {
          padding: 5rem 0;
          background: var(--background);
        }

        #testimonials-section h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          text-align: center;
          margin-bottom: 3rem;
        }

        #testimonial-carousel {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .carousel-arrow {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 50%;
          width: 3rem;
          height: 3rem;
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .carousel-arrow:hover {
          transform: scale(1.1);
          box-shadow: var(--shadow-glow);
        }

        .testimonial-main {
          display: flex;
          align-items: center;
          gap: 2rem;
          max-width: 600px;
        }

        .testimonial-photo {
          width: 5rem;
          height: 5rem;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid var(--primary);
        }

        .testimonial-text {
          flex: 1;
        }

        .testimonial-title {
          color: var(--primary);
          font-size: 1.125rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .testimonial-quote {
          color: var(--foreground);
          font-size: 1rem;
          margin-bottom: 0.75rem;
          line-height: 1.6;
        }

        .testimonial-author {
          color: var(--muted-foreground);
          font-weight: 600;
        }

        .testimonial-role {
          color: var(--muted-foreground);
          font-weight: 400;
          margin-left: 0.5rem;
        }

        /* Who Section */
        #who-section {
          padding: 5rem 0;
          background: #f8fafc;
        }

        #who-section h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          text-align: center;
          margin-bottom: 1rem;
        }

        .who-sub {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          text-align: center;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        #who-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto 3rem auto;
          padding: 0 1rem;
        }

        .who-item {
          background: var(--background);
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: var(--shadow-card);
          border: 1px solid var(--border);
          transition: all 0.2s;
          text-align: center;
        }

        .who-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgb(0 0 0 / 0.1);
        }

        .who-item img {
          width: 4rem;
          height: 4rem;
          margin-bottom: 1.5rem;
        }

        .who-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--foreground);
          margin-bottom: 0.75rem;
        }

        .who-desc {
          color: var(--muted-foreground);
          line-height: 1.6;
        }

        #who-cta {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          padding: 1rem 2.5rem;
          font-size: 1.125rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--shadow-glow);
          display: block;
          margin: 0 auto;
        }

        #who-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgb(16 185 129 / 0.4);
        }

        /* FAQ Section */
        #faq-section {
          padding: 5rem 0;
          background: var(--background);
        }

        #faq-section h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          text-align: center;
          margin-bottom: 1rem;
        }

        .faq-sub {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          text-align: center;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        #faq-accordion {
          max-width: 800px;
          margin: 0 auto;
        }

        .faq-item {
          background: var(--background);
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          box-shadow: var(--shadow-card);
          border: 1px solid var(--border);
          transition: all 0.2s;
        }

        .faq-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgb(0 0 0 / 0.1);
        }

        .faq-question {
          width: 100%;
          background: none;
          border: none;
          outline: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          cursor: pointer;
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--foreground);
        }

        .faq-icon {
          color: var(--primary);
          transition: transform 0.3s;
        }

        .faq-icon.open {
          transform: rotate(180deg);
        }

        .faq-answer-wrapper {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s;
        }

        .faq-answer-wrapper.open {
          max-height: 200px;
        }

        .faq-answer {
          color: var(--muted-foreground);
          padding: 0 1.5rem 1.5rem 1.5rem;
          line-height: 1.6;
        }

        /* Pricing Section */
        #pricing-section {
          padding: 5rem 0;
          background: #f8fafc;
        }

        #pricing-section h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          text-align: center;
          margin-bottom: 1rem;
        }

        .subtitle {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          text-align: center;
          margin-bottom: 3rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        #pricing-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .pricing-card {
          background: var(--background);
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: var(--shadow-card);
          border: 1px solid var(--border);
          transition: all 0.2s;
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .pricing-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgb(0 0 0 / 0.1);
        }

        .pricing-card.pro {
          background: var(--primary);
          color: var(--primary-foreground);
          border-color: var(--primary);
          transform: translateY(-1rem);
        }

        .popular-badge {
          position: absolute;
          top: -0.75rem;
          left: 50%;
          transform: translateX(-50%);
          background: var(--primary);
          color: var(--primary-foreground);
          padding: 0.5rem 1rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 700;
        }

        .pricing-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .pricing-card .price {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }

        .pricing-card .price span {
          font-size: 1rem;
          font-weight: 400;
          opacity: 0.8;
        }

        .pricing-card .description {
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .pricing-card ul {
          list-style: none;
          margin-bottom: 2rem;
          flex-grow: 1;
        }

        .pricing-card ul li {
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pricing-card ul li::before {
          content: "✓";
          color: var(--primary);
          font-weight: bold;
        }

        .pricing-card .cta-btn {
          width: 100%;
          padding: 1rem;
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: auto;
        }

        .pricing-card .cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgb(16 185 129 / 0.3);
        }

        .pricing-card.pro .cta-btn {
          background: var(--background);
          color: var(--foreground);
        }

        /* Waiting Section */
        #waiting-section {
          padding: 5rem 0;
          background: var(--background);
          text-align: center;
        }

        #waiting-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        #waiting-content h2 {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--foreground);
          margin-bottom: 1.5rem;
        }

        #waiting-content p {
          font-size: 1.125rem;
          color: var(--muted-foreground);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        #waiting-cta {
          background: var(--primary);
          color: var(--primary-foreground);
          border: none;
          border-radius: 9999px;
          padding: 1rem 2.5rem;
          font-size: 1.125rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--shadow-glow);
        }

        #waiting-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgb(16 185 129 / 0.4);
        }

        /* Footer */
        #footer {
          background: var(--foreground);
          color: var(--background);
          padding: 3rem 0 1rem 0;
        }

        #footer-main {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto 2rem auto;
          padding: 0 1rem;
        }

        .footer-logo {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--background);
          margin-bottom: 1rem;
        }

        .footer-social {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .footer-social a {
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .footer-social a:hover {
          opacity: 1;
        }

        .footer-social img {
          width: 1.5rem;
          height: 1.5rem;
          filter: invert(1);
        }

        .footer-disclaimer {
          font-size: 0.875rem;
          opacity: 0.8;
          line-height: 1.6;
        }

        .footer-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--background);
          margin-bottom: 1rem;
        }

        .footer-links a, .footer-service a {
          display: block;
          color: var(--background);
          text-decoration: none;
          margin-bottom: 0.5rem;
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .footer-links a:hover, .footer-service a:hover {
          opacity: 1;
        }

        .footer-payments {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }

        .footer-payments img {
          height: 1.5rem;
          width: auto;
          opacity: 0.8;
        }

        .footer-copyright {
          text-align: center;
          opacity: 0.8;
          font-size: 0.875rem;
          padding: 1rem 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin-top: 2rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          #intelligent-design-content {
            grid-template-columns: 1fr;
            gap: 2rem;
            text-align: center;
          }

          .intelligent-design-text h2 {
            font-size: 2rem;
          }

          .design-image {
            max-width: 100%;
          }

          #features-grid,
          #who-grid,
          #pricing-cards {
            grid-template-columns: 1fr;
          }

          .testimonial-main {
            flex-direction: column;
            text-align: center;
          }

          #menu-nav {
            display: none;
          }

          .options-dropdown {
            display: none;
          }
        }

        /* Fade/slide-in animation for sections */
        .fade-section {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.4,0,0.2,1), transform 0.8s cubic-bezier(0.4,0,0.2,1);
        }
        .fade-section.active {
          opacity: 1;
          transform: none;
        }
      `}</style>
    </div>
  );
};

export default Landing;
